import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { ApiResult } from 'common/models';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { CategoryDTO, PreviewEntityDTO } from 'common/models/data/entity.model';
import {
    CATEGORY_EDIT_FAIL,
    CATEGORY_EDIT_SUCCESS,
    FAILURE_HEADER,
    INDICATOR_EDIT_FAIL,
    INDICATOR_EDIT_SUCCESS,
    SUB_CATEGORY_EDIT_FAIL,
    SUB_CATEGORY_EDIT_SUCCESS,
    SUCCESS_HEADER
} from 'common/config';
import { EntityType, MessageType } from 'common/enums';
import { appStateActions } from 'common/redux/app-state';

import { previewDataActions } from './previewData.slice';
import { PreviewDataService } from './previewData.service';
import { SearchDTO, SearchVM } from 'common/models';
import { hasValue, previewDataMapper, previewIndicatorDataMapper } from 'common/utils/data';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { getPreviewData } from './previewData.selector';
import { findCategories } from 'common/utils/table';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { getUploadStatus } from '../file-upload/fileUpload.selector';
import { PreviewCategoriesVM } from 'common/models/data/previewData.model';

const loadPreviewData = function* ({ payload }: PayloadAction<PagedRequest>): any {
    let [categories, categoryErrors] = [
        { hasErrors: false, errors: '', value: { data: [], pagination: {} } },
        { hasErrors: false, errors: '', value: { data: [], pagination: {} } }
    ];
    const uploadFileDetails = yield select(getUploadStatus);
    if (uploadFileDetails.data.fileDetails?.targetId !== null) {
        [categories, categoryErrors] = yield all([
            call(PreviewDataService.getAllEntities, payload),
            call(PreviewDataService.getCategoriesErrorDetails, payload)
        ]);
    } else {
        [categories, categoryErrors] = yield all([
            call(PreviewDataService.getCategories, payload),
            call(PreviewDataService.getCategoriesErrorDetails, payload)
        ]);
    }

    if (
        (categories.hasErrors && categories.errors) ||
        (categoryErrors.hasErrors && categoryErrors.errors)
    ) {
        yield put({
            type: previewDataActions.loadPreviewDataError.type,
            payload: categories.errors
        });
    } else {
        let categoriesList: PreviewEntityDTO[] = [];
        if (
            categories.value.data &&
            categories.value.data.filter(
                (cat: PreviewCategoriesVM) => cat.entityType !== EntityType.Indicator
            ).length > 0
        ) {
            categoriesList = previewDataMapper(
                '',
                categories.value.data,
                categoryErrors.value.data
            );
        } else {
            categoriesList = previewIndicatorDataMapper(categories.value.data);
        }
        yield put({
            type: previewDataActions.loadPreviewDataSuccess.type,
            payload: {
                data: categoriesList,
                pagination: categories.value.pagination,
                errors: []
            }
        });
    }
};

const loadPreviewDataWatch = function* () {
    yield takeLatest(previewDataActions.loadPreviewData.type, loadPreviewData);
};

const getSearchList = function* ({ payload }: PayloadAction<SearchDTO>) {
    const apiResult: ApiResult<PaginatedApiResponse<SearchVM[]>> | undefined =
        yield PreviewDataService.getSearchList(payload);
    if (apiResult) {
        if (apiResult.hasErrors) {
            yield put({
                type: previewDataActions.searchListError
            });
        } else {
            yield put({
                type: previewDataActions.searchListSuccess.type,
                payload: apiResult.value
            });
        }
    } else {
        yield put({
            type: previewDataActions.searchListError.type
        });
    }
};

const getSearchListWatch = function* () {
    yield takeLatest(previewDataActions.searchList.type, getSearchList);
};

const updateCategory = function* ({ payload }: PayloadAction<CategoryDTO>) {
    const { hasErrors, errors, value }: ApiResult<CategoryDTO | IErrorApiResponse> =
        yield PreviewDataService.updateCategory(payload.categoryId, payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: previewDataActions.updateCategoryFailed.type,
            payload: error
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: CATEGORY_EDIT_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: previewDataActions.updateCategorySuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: CATEGORY_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const updateCategoryWatch = function* () {
    yield takeLatest(previewDataActions.updateCategory.type, updateCategory);
};
const updateSubCategory = function* ({ payload }: PayloadAction<CategoryDTO>) {
    const { hasErrors, errors, value }: ApiResult<CategoryDTO | IErrorApiResponse> =
        yield PreviewDataService.updateCategory(payload.categoryId, payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: previewDataActions.updateSubCategoryFailed.type,
            payload: error
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: SUB_CATEGORY_EDIT_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: previewDataActions.updateSubCategorySuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: SUB_CATEGORY_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const updateSubCategoryWatch = function* () {
    yield takeLatest(previewDataActions.updateSubCategory.type, updateSubCategory);
};

const updateIndicator = function* ({ payload }: PayloadAction<IndicatorUpdateDTO>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield PreviewDataService.updateIndicator(
        payload.indicatorId,
        payload
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: previewDataActions.updateIndicatorFailed.type,
            payload: error
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: INDICATOR_EDIT_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: previewDataActions.updateIndicatorSuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const updateIndicatorWatch = function* () {
    yield takeLatest(previewDataActions.updateIndicator.type, updateIndicator);
};

const fetchErrorCount = function* () {
    const { hasErrors, errors, value }: ApiResult<any> = yield PreviewDataService.getErrorCount();
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: previewDataActions.fetchErrorCountFailed.type,
            payload: error
        });
    } else {
        yield put({
            type: previewDataActions.fetchErrorCountSuccess.type,
            payload: value
        });
    }
};

const fetchErrorCountWatch = function* () {
    yield takeLatest(
        [
            previewDataActions.fetchErrorCount.type,
            previewDataActions.updateCategorySuccess.type,
            previewDataActions.updateSubCategorySuccess.type,
            previewDataActions.updateIndicatorSuccess.type,
            previewDataActions.deleteDataSuccess.type
        ],
        fetchErrorCount
    );
};

const deleteData = function* ({ payload }: PayloadAction<EntityDetailsPayload>) {
    let result: ApiResult<any>;
    if (payload.entityType === EntityType.Indicator) {
        result = yield PreviewDataService.deleteIndicator(payload.id);
    } else {
        result = yield PreviewDataService.deleteCategory(payload.id);
    }

    if (result.hasErrors && result.errors) {
        const error: string = result.errors[0];
        yield put({
            type: previewDataActions.deleteDataError.type,
            payload: error
        });
    } else {
        yield put({
            type: previewDataActions.deleteDataSuccess.type,
            payload
        });
    }
};

const deleteDataWatch = function* () {
    yield takeLatest(previewDataActions.deleteData.type, deleteData);
};

const getEntitiesByCategory = function* ({ payload }: PayloadAction<PreviewEntityDTO>): any {
    const previewData = yield select(getPreviewData);
    const subCats = findCategories(previewData.data.data, payload.id);
    if (!payload.isExpanded && !hasValue(subCats.subCategories)) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        const [categories, categoryErrors] = yield all([
            call(PreviewDataService.getEntities, payload.id),
            call(PreviewDataService.getCategoriesErrorDetailsByEntity, payload.id)
        ]);
        if (
            (categories.hasErrors && categories.errors) ||
            (categoryErrors.hasErrors && categoryErrors.errors)
        ) {
            yield put({
                type: previewDataActions.toggleExpansionFailed.type,
                payload: categories.error
            });
        } else {
            const categoriesList = previewDataMapper(
                payload.id,
                categories.value,
                categoryErrors.value
            );
            yield put({
                type: previewDataActions.toggleExpansionSuccess.type,
                payload: {
                    parent: payload,
                    subCategories: categoriesList
                }
            });
        }
    } else {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

const getEntitiesByCategoryWatch = function* () {
    yield takeLatest(previewDataActions.toggleExpansion.type, getEntitiesByCategory);
};

export {
    loadPreviewDataWatch,
    getSearchListWatch,
    updateCategoryWatch,
    updateSubCategoryWatch,
    updateIndicatorWatch,
    fetchErrorCountWatch,
    deleteDataWatch,
    getEntitiesByCategoryWatch
};
